// src/pages/design/Dcn_view.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import FormViewManager from "../../components/common/form/FormViewManager";
import TableCell from "../../components/common/ui/TableCell";
import { applyDesignFilesFromDCN } from "../../api/design/file";
import PeopleView from "../../components/common/people/PeopleView";
import { getDocPersons } from "../../api/papers";
import { changeDocState } from "../../api/design/meta"; // ⭐ 상태 변경 API
import ApprovalActionDoc from "../../components/approval/ApprovalAction_doc";
import SignTableDoc from "../../components/approval/SignTable_doc";

export default function Dcn_view() {
  const { id: project, dns } = useParams();

  return (
    <FormViewManager
      params={{
        module: "design",
        doc_type: "DCN",
        project,
        dns,
      }}
      doc_name={"설계변경도면"}
      doc_eng={"Design Change Notice"}
    >
      <DcnViewContent project={project} dns={dns} />
    </FormViewManager>
  );
}

function DcnViewContent({ meta, data, project, dns }) {
  const columns = [
    { key: "no", header: "No", width: "60px", align: "center" },
    { key: "drawingNo", header: "도면번호" },
    { key: "version", header: "버전" },
    { key: "changeDesc", header: "변경 내용" },
    { key: "file", header: "첨부 도면" },
  ];

  const rows = (meta?.details || []).map((r, i) => ({
    no: i + 1,
    ...r,
  }));

  // ----------------------------------------------------------------------------------------
  // 담당자 로드
  // ----------------------------------------------------------------------------------------
  const me = JSON.parse(localStorage.getItem("user") || "{}");
  const myUserId = me?.id || me?.user?.id;

  const [people, setPeople] = useState([]);

  useEffect(() => {
    const loadPeople = async () => {
      try {
        const list = await getDocPersons(dns);
        setPeople(list || []);
      } catch (error) {
        console.error("❌ 담당자 로드 오류:", error);
        setPeople([]);
      }
    };
    loadPeople();
  }, [dns]);

  const isAssignee = people.some(
    (p) => p.id === myUserId || p.userId === myUserId || p.user_id === myUserId
  );

  // ----------------------------------------------------------------------------------------
  // 상태(state/status)
  // ----------------------------------------------------------------------------------------
  const docState =
    data?.state ?? meta?.state ?? data?.status ?? meta?.status ?? null;

  const canReject = isAssignee && docState === "save";

  // ----------------------------------------------------------------------------------------
  // DCN 반려
  // ----------------------------------------------------------------------------------------
  const handleReject = async () => {
    if (!window.confirm("DCN 요청을 반려하시겠습니까?")) return;
    try {
      await changeDocState(dns, "reject");
      alert("반려 처리되었습니다. 담당자가 다시 수정할 수 있습니다.");
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert("반려 처리 중 오류가 발생했습니다.");
    }
  };

  // ----------------------------------------------------------------------------------------
  // 라이브러리 반영
  // ----------------------------------------------------------------------------------------
  const handleApplyToLibrary = async () => {
    console.log("📦 [Apply DCN to Library] 호출");
    console.log("➡ project:", project);
    console.log("➡ dns:", dns);
    console.log("➡ files:", data?.files);

    const fileCount = data?.files?.length || 0;

    const confirmText =
      fileCount > 0
        ? `총 ${fileCount}개의 도면을 라이브러리에 반영하시겠습니까?`
        : "첨부된 도면이 없습니다.\n그래도 반영 처리(기록)는 진행됩니다.";

    if (!window.confirm(confirmText)) return;

    try {
      const res = await applyDesignFilesFromDCN(project, dns);
      console.log("📥 API Response:", res);

      // ⭐ 상태 complete 로 변경
      await changeDocState(dns, "complete");

      alert(res.message || "라이브러리 반영 완료 및 문서 상태 완료 처리");
      window.location.reload();
    } catch (err) {
      console.error("❌ DCN 라이브러리 반영 실패:", err);
      alert("라이브러리 반영 실패");
    }
  };

  return (
    <div className="text-[13px]">
      {/* 상단 버튼 + 제목 */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-body-bold">발행 정보</h2>

        {/* 상태 변경 버튼 */}
        <div className="flex gap-2 no-print">
          {canReject && (
            <button
              onClick={handleReject}
              className="px-3 py-1 border rounded text-sm hover:bg-gray-100 text-red-600"
            >
              반려
            </button>
          )}
        </div>
      </div>

      {/* 기본정보 테이블 */}
      <div className="overflow-hidden mb-6 text-[13px]">
        <table className="w-full border-collapse table-fixed">
          <colgroup>
            <col style={{ width: "90px" }} />
            <col style={{ width: "40%" }} />
            <col style={{ width: "90px" }} />
            <col style={{ width: "40%" }} />
          </colgroup>

          <tbody>
            <tr>
              <TableCell
                label="발행 사유"
                rich={true}
                value={meta?.reason}
                colSpan={3}
              />
            </tr>
          </tbody>
        </table>
      </div>

      {/* 변경도면 표시 */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-body-bold">변경 도면 내역</h2>
        <button
          onClick={handleApplyToLibrary}
          className="px-3 py-1 border rounded-md bg-white hover:bg-gray-50 text-sm no-print"
        >
          변경도면 라이브러리에 적용
        </button>
      </div>

      <div className="border-l border-t border-b overflow-hidden border-black">
        <table className="w-full border-collapse text-xs leading-relaxed">
          <thead className="doc-thead">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`doc-th ${c.align === "center"
                      ? "text-center"
                      : c.align === "right"
                        ? "text-right"
                        : "text-left"
                    }`}
                  style={{ width: c.width }}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((r) => (
                <tr key={r.no} className="border-t border-r  border-black">
                  {columns.map((c) => {
                    if (c.key === "drawingNo") {
                      const fileUrl =
                        r.drawingFileUrl ||
                        (r.drawingFileId
                          ? `/project/${project}/design/detail?doc=${r.drawingFileId}`
                          : null
                        );


                      return (
                        <td key={c.key} className="px-3 py-2 text-left border-r border-black">
                          {fileUrl ? (
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 underline hover:text-indigo-800"
                            >
                              {r.drawingNo || "도면보기"}
                            </a>
                          ) : (
                            <span>{r.drawingNo || "-"}</span>
                          )}
                        </td>
                      );
                    }

                    if (c.key === "file") {
                      const idx = Number(r.index ?? r.no - 1);
                      const file = data.files?.[idx];

                      return (
                        <td key={c.key} className="px-3 py-2 item-center ">
                          {file ? (
                            <a
                              href={file.file_url}
                              target="_blank"
                              rel="noreferrer"
                              className="underline text-blue-600"
                            >
                              {file.file_name || "파일 보기"}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                      );
                    }

                    return (
                      <td
                        key={c.key}
                        className={`px-3 py-2 border-r border-black ${c.align === "center"
                            ? "text-center"
                            : c.align === "right"
                              ? "text-right"
                              : "text-left"
                          }`}
                      >
                        {r[c.key] || "-"}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-4 text-sm"
                >
                  변경 도면 내역이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 연결 안내 */}
      {meta?.related_doc && (
        <div className="mt-4 text-sm italic">
          ※ 본 문서는 FCN ({meta.related_doc}) 통보서를 기반으로 발행되었습니다.
        </div>
      )}

      <div className="mt-8">
        <ApprovalActionDoc dns={dns} />
      </div>
      <SignTableDoc dns={dns} />
    </div>
  );
}
